import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  RESTART_POLICY_KEY,
  restartPolicyQuery,
  saveRestartPolicy,
  type RestartPolicy,
  type RestartPolicyPage,
} from '../../api/settingsSoftsee'
import { toast } from '../../ui/notify'
import { SettingsCheck } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Softsee > Restarts - politica de restart a firmei (cerinta 60 /
 * R64): cu cat inainte e anuntat omul, de cate ori si cu cat poate amana,
 * fereastra in care e voie, ce se intampla la termen si mesajul de pe ecran.
 *
 * Rezumatul de sus il scrie serverul (RestartPolicy::summary) - ACELASI text
 * pe care il arata si formularul de instalare programata, ca cele doua sa nu
 * se poata contrazice.
 */
export function RestartPolicyPanel() {
  const page = useQuery(restartPolicyQuery())
  if (page.error) {
    const status = page.error instanceof ApiError ? page.error.status : 0
    return (
      <div className="alert alert-danger" role="alert">
        {status === 403 ? 'You do not have access to this action.' : page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <RestartPolicyForm page={page.data} />
}

type Draft = Record<keyof RestartPolicy, string>

function draftOf(policy: RestartPolicy): Draft {
  return {
    warn_minutes: String(policy.warn_minutes),
    postpone_max: String(policy.postpone_max),
    postpone_minutes: String(policy.postpone_minutes),
    window_start: policy.window_start,
    window_end: policy.window_end,
    force_after_deadline: policy.force_after_deadline ? '1' : '0',
    message: policy.message,
  }
}

function RestartPolicyForm({ page }: { page: RestartPolicyPage }) {
  const client = useQueryClient()
  const [draft, setDraft] = useState<Draft>(() => draftOf(page.policy))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const readOnly = !page.can_edit
  const set = (key: keyof Draft) => (value: string) => setDraft((d) => ({ ...d, [key]: value }))

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (busy || readOnly) return
    setBusy(true)
    setErrors({})
    try {
      const saved = await saveRestartPolicy({ ...draft, force_after_deadline: draft.force_after_deadline === '1' })
      client.setQueryData<RestartPolicyPage>(RESTART_POLICY_KEY, { ...page, policy: saved.policy, summary: saved.summary, saved: true })
      setDraft(draftOf(saved.policy))
      toast('The restart policy was saved.', 'Saved', 'success')
    } catch (err) {
      if (err instanceof ApiError) {
        const fields = err.details?.errors
        if (fields && typeof fields === 'object') setErrors(fields as Record<string, string>)
        toast(err.message || 'Not saved', 'Error', 'error')
      } else {
        toast('Not saved', 'Error', 'error')
      }
    } finally {
      setBusy(false)
    }
  }

  const number = (key: 'warn_minutes' | 'postpone_max' | 'postpone_minutes', label: string, unit: string, help: string) => (
    <div className={errors[key] ? 'form-group has-error' : 'form-group'}>
      <label htmlFor={`rp-${key}`}>{label}</label>
      <div>
        <input
          id={`rp-${key}`}
          type="number"
          className="form-control input-sm"
          style={{ width: 100, display: 'inline-block' }}
          min={page.limits[key][0]}
          max={page.limits[key][1]}
          value={draft[key]}
          disabled={readOnly}
          onChange={(e) => set(key)(e.target.value)}
        />{' '}
        <span>{unit}</span>
      </div>
      <span className="help-block">{errors[key] ?? help}</span>
    </div>
  )

  return (
    <div className="panel panel-default" style={{ maxWidth: 760 }}>
      <div className="panel-heading">
        <strong>Restarts</strong> - how a scheduled restart is announced and carried out
      </div>
      <div className="panel-body">
        <div className="alert alert-info" role="status" data-testid="restart-summary">
          <i className="fas fa-info-circle" aria-hidden="true"></i> {page.summary}
        </div>
        <p className="text-muted" style={{ fontSize: 12 }}>
          Applies to restarts after scheduled software installations and operating system updates, and to restarts sent
          from Remote Operations with a delay. Agents older than {page.min_agent} cannot show the notice: Windows gets the
          system warning (5 minutes, no postponing) and a request without a deadline is not sent; Linux and macOS restart
          at the deadline without a warning.
        </p>
        <form onSubmit={onSubmit} noValidate>
          {number('warn_minutes', 'Warn the person at the computer', 'minutes before', 'They see the notice with a countdown from this moment. 0 = only when the restart is due.')}
          {number('postpone_max', 'Postponements allowed', 'times', 'How many times the "Postpone" button can be pressed. 0 = no button.')}
          {number('postpone_minutes', 'One postponement lasts', 'minutes', 'Also the interval between reminders when the restart is not forced.')}

          <div className={errors.window_start ? 'form-group has-error' : 'form-group'}>
            <label htmlFor="rp-window-start">Restart only between</label>
            <div>
              <input
                id="rp-window-start"
                type="time"
                className="form-control input-sm"
                style={{ width: 120, display: 'inline-block' }}
                value={draft.window_start}
                disabled={readOnly}
                onChange={(e) => set('window_start')(e.target.value)}
              />{' '}
              and{' '}
              <input
                id="rp-window-end"
                aria-label="Window end"
                type="time"
                className="form-control input-sm"
                style={{ width: 120, display: 'inline-block' }}
                value={draft.window_end}
                disabled={readOnly}
                onChange={(e) => set('window_end')(e.target.value)}
              />
            </div>
            <span className="help-block">
              {errors.window_start ??
                "On the computer's clock. An end earlier than the start means overnight (22:00 - 06:00). Leave both empty to allow restarts at any time."}
            </span>
          </div>

          <div className="form-group">
            <SettingsCheck
              id="rp-force"
              checked={draft.force_after_deadline === '1'}
              disabled={readOnly}
              onChange={(on: boolean) => set('force_after_deadline')(on ? '1' : '0')}
              label="Restart on its own when the time is up"
            />
            <span className="help-block">
              Unchecked: the computer is never restarted without the person; they are reminded until they restart it.
            </span>
          </div>

          <div className={errors.message ? 'form-group has-error' : 'form-group'}>
            <label htmlFor="rp-message">Message on the screen</label>
            <textarea
              id="rp-message"
              className="form-control"
              rows={3}
              maxLength={500}
              placeholder={page.default_message}
              value={draft.message}
              disabled={readOnly}
              onChange={(e) => set('message')(e.target.value)}
            />
            <span className="help-block">{errors.message ?? 'Empty = the text shown in grey.'}</span>
          </div>

          {readOnly ? (
            <p className="text-muted">You can see this policy but not change it.</p>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={busy}>
              <i className="fas fa-save" aria-hidden="true"></i> Save
            </button>
          )}
        </form>
      </div>
    </div>
  )
}
